import React, { useRef } from 'react';
import { Input } from './input';
import { X } from 'lucide-react';

/**
 * Input de hora com digitação corrida
 * 
 * REGRAS DE OURO:
 * - Digitação direta: 4 dígitos (0400) → auto-formata para HH:MM (04:00)
 * - Enter: avança para próximo campo
 * - Backspace em campo vazio: volta para campo anterior
 * - Botão X ou campo vazio: exclui a marcação do slot
 */
const InputHora = React.forwardRef(({ 
  value, 
  onChange, 
  onClear,
  onEnter,
  onBackEmpty,
  showClear = true,
  className, 
  ...props 
}, ref) => {
  const inputRef = useRef(null);
  
  const handleChange = (e) => {
    let val = e.target.value;
    
    // Remover tudo que não é dígito
    const digits = val.replace(/\D/g, '');
    
    // Limitar a 4 dígitos
    const limitedDigits = digits.slice(0, 4);
    
    // Auto-formatar para HH:MM quando tiver 4 dígitos
    let formatted = limitedDigits;
    if (limitedDigits.length >= 3) {
      formatted = limitedDigits.slice(0, 2) + ':' + limitedDigits.slice(2, 4);
    } else if (limitedDigits.length === 2 && val.includes(':')) {
      // Usuário digitou ":" manualmente após 2 dígitos
      formatted = limitedDigits + ':';
    }
    
    // Criar evento sintético
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: formatted
      }
    };
    onChange && onChange(syntheticEvent);
  };

  const handleKeyDown = (e) => {
    // Enter: avança para próximo campo
    if (e.key === 'Enter') {
      e.preventDefault();
      onEnter && onEnter();
      return;
    }
    
    // Backspace em campo vazio: volta para campo anterior
    if (e.key === 'Backspace' && !value) {
      e.preventDefault();
      onBackEmpty && onBackEmpty();
      return;
    }
    
    // Delete: limpa o campo
    if (e.key === 'Delete') {
      e.preventDefault();
      handleClear();
      return;
    }
  };

  const handleClear = () => {
    const syntheticEvent = {
      target: { value: '' }
    };
    onChange && onChange(syntheticEvent);
    onClear && onClear();
  };

  const hasValue = value && value.length > 0;

  return (
    <div className="relative flex items-center">
      <Input
        ref={ref || inputRef}
        type="text"
        inputMode="numeric"
        placeholder="--:--"
        value={value || ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        maxLength={5}
        className={`text-center font-mono pr-7 ${className || ''}`}
        {...props}
      />
      {showClear && hasValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-1.5 p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
          tabIndex={-1}
          title="Limpar (excluir marcação)"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
});

InputHora.displayName = 'InputHora';

export { InputHora };
